import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import hexGrid from '@turf/hex-grid';
import area from '@turf/area';
import intersect from '@turf/intersect';
import booleanDisjoint from '@turf/boolean-disjoint';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import booleanWithin from '@turf/boolean-within';
import union from '@turf/union';
import centroid from '@turf/centroid';
import * as turf from '@turf/helpers';

import {
  ADR,
  BarGraphData,
  Coordinates,
  Listing,
  ListingLegacy,
  ListingVal,
  Listings,
  OTAListing,
  OccupancyData,
  OccupancyRate,
  Revenue,
} from 'src/interfaces/idashboard';
import {
  abbreviateNumber,
  calculateMoMChange,
  convertCurrency,
  getLTMData,
  getMonthlyKeyMetrics,
  isListingLegacy,
  processLastMonthData,
  processPolygon,
  getKeyMetricsValByPercentile,
  checkListingRecentlyAdded,
  checkListingInactive,
  checkListingUnlisted,
} from 'src/utils/functions';

import * as d3 from 'd3';
import {
  amenitiesObject,
  exchangeRates,
  sampleMedianNightlyRateData,
  sampleOccupancyRateData,
  sampleRevenueData,
} from 'src/const';
import {
  COLOR_ERROR,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_200,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_800,
  COLOR_SUCCESS,
  COLOR_WARNING,
  NEIGHBOURHOOD_THEME,
} from 'src/colors';

const ss = require('simple-statistics');

export const getMarketLevelAggregateData = (
  listings,
  isDisabled,
  renderIn,
  title,
  currency,
  lastMonthData,
  percentile
) => {
  const unit = getUnit(title, currency);

  const metricsKey = getMetricsKey(title, false);

  const legacyMetricsKey = getMetricsKey(title);

  if (listings.length > 0 && lastMonthData && lastMonthData === 'process') {
    lastMonthData = processLastMonthData(
      listings,
      legacyMetricsKey as 'occupancy_rate' | 'revenue',
      percentile
    );
  }

  let keyMetricsVal = -1;
  if (listings.length > 0) {
    keyMetricsVal = getKeyMetricsVal(
      listings as Listing[],
      listings as Listing[],
      false,
      currency,
      title,
      unit,
      metricsKey,
      isDisabled,
      lastMonthData,
      percentile
    );
  }
  return keyMetricsVal;
};

export const getScoreColorAndValue = (scoreVal: number, maxValue: number, minValue: number) => {
  const range = maxValue - minValue;
  const normalizedScore = ((scoreVal - minValue) / range) * 100;

  if (normalizedScore < 20) {
    return { color: COLOR_PRIMARY_1_200, score: normalizedScore };
  } else if (normalizedScore >= 20 && normalizedScore < 40) {
    return { color: COLOR_PRIMARY_1_400, score: normalizedScore };
  } else if (normalizedScore >= 40 && normalizedScore < 60) {
    return { color: COLOR_PRIMARY_1_600, score: normalizedScore };
  } else if (normalizedScore >= 60 && normalizedScore < 80) {
    return { color: COLOR_PRIMARY_1_800, score: normalizedScore };
  } else {
    return { color: COLOR_PRIMARY_1, score: normalizedScore };
  }
};

export const getColorWithRangeForMarketGrad = (minValue, maxValue) => {
  const range = maxValue - minValue;
  const result: any = [];

  const colors = [
    { color: '#C4E7E5', range: 20 },
    { color: '#9DD7D4', range: 40 },
    { color: '#75C7C3', range: 60 },
    { color: '#4EB7B2', range: 80 },
    { color: '#3AAFA9', range: 100 },
  ];

  for (let i = minValue; i <= maxValue; i += range / 5) {
    for (let j = 0; j < colors.length; j++) {
      const { color, range: colorRange } = colors[j];
      if (i < minValue + (colorRange / 100) * range) {
        result.push({ key: i.toFixed(1), value: color });
        break;
      }
    }
  }

  // Sort the array by key in descending order
  result.sort((a, b) => b.key - a.key);

  return result;
};

export const getMinmaxROI = discoveryMarkets => {
  if (discoveryMarkets.length === 0) {
    return [0, 0];
  }
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (let data of discoveryMarkets) {
    if (data.ten_years_roi < minValue) {
      minValue = data.ten_years_roi;
    }
    if (data.ten_years_roi > maxValue) {
      maxValue = data.ten_years_roi;
    }
  }
  return [minValue, maxValue];
};

export const processCompareData = (
  marketData,
  isMarketDataAccessible,
  freeMarketDataKey,
  currency
) => {
  const fullDataNotPresent =
    !isMarketDataAccessible && !marketInSampleFreeMarkets(marketData, freeMarketDataKey);

  return marketData
    ? [
        marketData['district'],
        marketData['ten_years_roi'],
        marketData['city'],
        marketData['country_code'],
        fullDataNotPresent,
        fullDataNotPresent ? '200%' : marketData['net_rental_yield'].toFixed(2) + '%',
        marketData['revenue'],
        fullDataNotPresent ? '500000' : marketData['property_price'],
        fullDataNotPresent ? '200%' : marketData['annual_appreciation'] + '%',
        fullDataNotPresent ? '200%' : marketData['population_change'] + '%',
        'Coming Soon',
        fullDataNotPresent ? '200%' : marketData['income_change'] + '%',
        fullDataNotPresent ? '200%' : marketData['property_price_change'] + '%',
        fullDataNotPresent ? '500000' : marketData['average_expense'],
        marketData['adr'],
        marketData['occupancy_rate'] + '%',
        marketData['country'] ? marketData['country'].toLowerCase().replace(' ', '-') : '0',
      ]
    : [];
};

const getKeyMetricsVal = (
  data: ADR[] | OccupancyRate[] | Revenue[] | Listing[],
  listing: Listing[] | ListingLegacy[],
  isLegacy: boolean,
  currency: string,
  title: string,
  unit: string,
  metricsKey: string,
  isDisabled: boolean,
  lastMonthData?: string,
  percentile?: number
) => {
  let rawKeyMetricsVal = getRawKeyMetricsVal(
    lastMonthData,
    isDisabled,
    isLegacy,
    metricsKey,
    listing,
    percentile,
    data,
    title
  );
  if (title.includes('revenue')) {
    convertCurrency(currency, rawKeyMetricsVal);
  }
  return rawKeyMetricsVal;
};

const getRawKeyMetricsVal = (
  lastMonthData,
  isDisabled,
  isLegacy,
  metricsKey,
  listing,
  percentile,
  data,
  title
) => {
  if (metricsKey.includes('revenue')) {
    const medianAdrLTM = getKeyMetricsValByPercentile(data as Listing[], 'adrLTM', percentile);
    const medianOccupancyRateLTM = getKeyMetricsValByPercentile(
      data as Listing[],
      'occupancyRateLTM',
      percentile
    );
    // const medianRevenueFromCleaningFeeLTM = getKeyMetricsValByPercentile(
    //   data as Listing[],
    //   'revenueFromCleaningFeeLTM',
    //   percentile
    // );
    const medianCleaningFee = getKeyMetricsValByPercentile(
      data as Listing[],
      'cleaningFee',
      percentile
    );
    const medianBookingCount = getKeyMetricsValByPercentile(
      data as Listing[],
      'numberOfBookingsLTM',
      percentile
    );
    const revenue = Math.round(
      // medianAdrLTM * (medianOccupancyRateLTM / 100) * 365 + medianRevenueFromCleaningFeeLTM
      medianAdrLTM * (medianOccupancyRateLTM / 100) * 365 + medianCleaningFee * medianBookingCount
    );
    return revenue;
  }
  return getKeyMetricsValByPercentile(data as Listing[], metricsKey, percentile);
};

const getMetricsKey = (title: string, isLegacy = true) => {
  if (title.includes('Nightly Rate')) {
    return isLegacy ? 'adr' : 'adrLTM';
  } else if (title.includes('Occupancy')) {
    return isLegacy ? 'occupancy_rate' : 'occupancyRateLTM';
  } else if (title.includes('Cleaning')) {
    return isLegacy ? 'cleaning_fee' : 'cleaningFee';
  } else if (title.includes('Booking')) {
    return isLegacy ? 'booking' : 'numberOfBookingsLTM';
  } else {
    return isLegacy ? 'revenue' : 'revenuePotential';
  }
};

const getUnit = (titleVal: string, currency: string): string => {
  if (titleVal.includes('Occupancy')) {
    return '%';
  } else {
    return exchangeRates[currency].symbol;
  }
};

export const getHistoricalData = (historicalMarketPerformanceData, metricsKey) => {
  let keys;

  if (metricsKey == 'occupancy') {
    keys = ['occupancy_rate25', 'occupancy_rate50', 'occupancy_rate75', 'occupancy_rate90'];
  } else if (metricsKey == 'revenue') {
    keys = ['revenue25', 'revenue50', 'revenue75', 'revenue90'];
  } else if (metricsKey == 'daily') {
    keys = ['daily_rate25', 'daily_rate50', 'daily_rate75', 'daily_rate90'];
  } else {
    keys = ['active_listings_count'];
  }

  let historicalData: any = [];

  for (const date in historicalMarketPerformanceData) {
    let tempData = {
      date: date,
      [keys[0]]:
        historicalMarketPerformanceData[date][keys[0]] == null
          ? null
          : historicalMarketPerformanceData[date][keys[0]],
      [keys[1]]:
        historicalMarketPerformanceData[date][keys[1]] == null
          ? null
          : historicalMarketPerformanceData[date][keys[1]],
      [keys[2]]:
        historicalMarketPerformanceData[date][keys[2]] == null
          ? null
          : historicalMarketPerformanceData[date][keys[2]],
      [keys[3]]:
        historicalMarketPerformanceData[date][keys[3]] == null
          ? null
          : historicalMarketPerformanceData[date][keys[3]],
    };
    historicalData.push(tempData);
  }

  return formatHistoricalData(historicalData.reverse(), keys);
};

const formatHistoricalData = (historicalData, keys) => {
  let aggregatedData = agrregateHistoricaldata(historicalData, keys);

  let flattenData = flattenAggregateData(aggregatedData);

  let sortFlattenData = sortFlattenDataAsPerLatestDataAvailiable(flattenData, historicalData);

  let YoYChange = getYoYChange(sortFlattenData, keys);

  return [sortFlattenData, YoYChange];
};

const sortFlattenDataAsPerLatestDataAvailiable = (flattenData, historicalData) => {
  const sortedHistorical = historicalData.sort((a, b) => +new Date(b.date) - +new Date(a.date));
  const latestDateInHistoricalData = sortedHistorical[0].date;
  let latestMonth = getMonthAbbreviation(latestDateInHistoricalData.split('-')[1]);

  const monthOrder = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const latestMonthIndex = monthOrder.indexOf(latestMonth);

  const dynamicMonthOrder = [
    ...monthOrder.slice(latestMonthIndex + 1),
    ...monthOrder.slice(0, latestMonthIndex + 1),
  ];

  const sortedArray = flattenData.sort((a, b) => {
    const aIndex = dynamicMonthOrder.indexOf(a.month);
    const bIndex = dynamicMonthOrder.indexOf(b.month);

    return aIndex - bIndex;
  });
  return sortedArray;
};

export const flattenAggregateData = aggregatedData => {
  let flattenData: any = [];
  for (const key of Object.keys(aggregatedData)) {
    let temp = {};
    temp['month'] = getMonthAbbreviation(key);
    for (const year of Object.keys(aggregatedData[key])) {
      temp[year] = aggregatedData[key][year];
    }
    flattenData.push(temp);
  }
  return flattenData;
};

const agrregateHistoricaldata = (historicalData, keys) => {
  let monthWithAggregatedYearlyData = {};
  for (let data of historicalData) {
    let date = data.date;
    let month = date.split('-')[1];
    let year = date.split('-')[0];

    if (!monthWithAggregatedYearlyData.hasOwnProperty(month)) {
      monthWithAggregatedYearlyData[month] = {};
    }

    if (!monthWithAggregatedYearlyData[month].hasOwnProperty(year)) {
      monthWithAggregatedYearlyData[month][year] = {};
    }

    for (const key of keys) {
      monthWithAggregatedYearlyData[month][year][key] = data[key];
    }
  }

  // Aggregate data for LTMData, last2YearsData, and last3YearsData
  for (let month in monthWithAggregatedYearlyData) {
    let latestYear = 0;
    let previousYear = 0;
    let twoYearsAgo = 0;
    let LTMData;
    let last2YearsData;
    let last3YearsData;

    // Find the latest year, previous year, and the year before that
    for (let key in monthWithAggregatedYearlyData[month]) {
      if (Number(key) > latestYear) {
        twoYearsAgo = previousYear;
        previousYear = latestYear;
        latestYear = Number(key);
      } else if (Number(key) > previousYear) {
        twoYearsAgo = previousYear;
        previousYear = Number(key);
      } else if (Number(key) > twoYearsAgo) {
        twoYearsAgo = Number(key);
      }
    }

    LTMData = monthWithAggregatedYearlyData[month][latestYear];
    last2YearsData = monthWithAggregatedYearlyData[month][previousYear];
    last3YearsData = monthWithAggregatedYearlyData[month][twoYearsAgo];

    monthWithAggregatedYearlyData[month]['LTMData'] = LTMData;
    monthWithAggregatedYearlyData[month]['last2YearsData'] = last2YearsData;
    monthWithAggregatedYearlyData[month]['last3YearsData'] = last3YearsData;
    monthWithAggregatedYearlyData[month]['latestYear'] = latestYear;
    monthWithAggregatedYearlyData[month]['previousYear'] = previousYear;
    monthWithAggregatedYearlyData[month]['twoYearsBefore'] = twoYearsAgo;
    monthWithAggregatedYearlyData[month]['monthYear'] =
      getMonthAbbreviation(month) + ',' + (latestYear % 100);
  }

  return monthWithAggregatedYearlyData;
};

const getMonthAbbreviation = month => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Ensure the month is a valid number between 1 and 12
  const numericMonth = parseInt(month, 10);
  if (isNaN(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    throw new Error('Invalid month value');
  }

  // Return the three-letter abbreviation
  return months[numericMonth - 1];
};

// const getOccupancyLastYear = (targetDate: string, dataArray: OccupancyData[]): number => {
//   const lastYearDate = new Date(targetDate);
//   lastYearDate.setFullYear(lastYearDate.getFullYear() - 1); // Get the same month of the previous year

//   const lastYearMonth = `${lastYearDate.getFullYear()}-${(lastYearDate.getMonth() + 1)
//     .toString()
//     .padStart(2, '0')}`;

//   const lastYearData = dataArray.find(item => item.date === lastYearMonth);
//   return lastYearData ? lastYearData.occupancy : 0;
// };

export const formatDateString = (inputString: string): string => {
  const date = new Date(`${inputString}-01`);
  const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
  const formattedDate = date.toLocaleDateString(undefined, options);
  return formattedDate;
};

export const getYoYChange = (data, keys) => {
  let totalCurrentYeardata = 0;
  let totalPastYearData = 0;

  for (let monthlyData of data) {
    for (let key of keys) {
      if (key.includes('50') || key === 'active_listings_count') {
        totalCurrentYeardata += +monthlyData.LTMData[key];
        totalPastYearData += +monthlyData.last2YearsData[key];
      }
    }
  }

  if (totalPastYearData > 0) {
    let percentage = (
      ((totalCurrentYeardata - totalPastYearData) / totalPastYearData) *
      100
    ).toFixed(1);
    return percentage;
  } else {
    return 'N/A';
  }
};

export const calculatePolygonCentroid = vertices => {
  let n = vertices.length;
  let A = 0; // Area initialization
  let Cx = 0; // Centroid x-coordinate initialization
  let Cy = 0; // Centroid y-coordinate initialization

  for (let i = 0; i < n; i++) {
    let lng_i = vertices[i].lng;
    let lat_i = vertices[i].lat;
    let lng_next = vertices[(i + 1) % n].lng;
    let lat_next = vertices[(i + 1) % n].lat;

    A += lng_i * lat_next - lng_next * lat_i;
    Cx += (lng_i + lng_next) * (lng_i * lat_next - lng_next * lat_i);
    Cy += (lat_i + lat_next) * (lng_i * lat_next - lng_next * lat_i);
  }

  A *= 0.5; // Divide the area by 2
  Cx = Cx / (6 * A);
  Cy = Cy / (6 * A);

  return { lng: Cx, lat: Cy };
};

export const extractLatLong = coordinate => {
  // Remove "POINT(" and ")"
  const cleanCoordinate = coordinate.substring(6, coordinate.length - 1);
  // Split by space to separate latitude and longitude
  const [longitude, latitude] = cleanCoordinate.split(' ');
  // Return latitude and longitude as numbers
  return { latitude: parseFloat(latitude), longitude: parseFloat(longitude) };
};

export const getScale = zoom => {
  if (zoom >= 14) {
    return 1.2;
  } else if (zoom >= 12) {
    return 1;
  } else if (zoom >= 10) {
    return 0.8;
  } else if (zoom >= 8) {
    return 0.6;
  } else if (zoom >= 6) {
    return 0.4;
  } else {
    return 0.2;
  }
};

export const getPolygonPaths = (polygonRaw: string, multiPolygonRaw: string) => {
  const polygonArr: Coordinates[][] = processPolygon(polygonRaw, multiPolygonRaw);
  let paths: Coordinates[][] = [];
  if (polygonArr.length > 0) {
    polygonArr.forEach((polygon: Coordinates[]) => paths.push(polygon));
  }
  return paths;
};

export const generateHexGrid = (
  polygonRaw: string,
  multiPolygonRaw: string,
  listings: Listings
) => {
  // transform polygon string to google maps polygon paths... [{lat: 0, lng: 0},{lat: 0, lng: 0},..]
  const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
  // transfrom google maps polygon paths to array of linear rings... [[0,0],[0,0],..]
  const coordinates = paths[0].map(coords => [coords.lng, coords.lat]);
  // transfrom array of linear rings to RFC Polygon feature
  const turfPolygon = turf.polygon([coordinates]);
  // logic to dynamically calculate hexagon size
  const marketArea = area(turfPolygon) * 0.000001; // sqkm
  const numberOfHexagons = Object.values(listings).length / 50;
  const areaOfEachHexagon = marketArea / numberOfHexagons;
  const hexSideLength = Math.sqrt((3 * areaOfEachHexagon) / (2 * Math.sqrt(3)));

  // created a boundary box from the polygon
  const boundaryBox = bbox(turfPolygon);
  // creates hexgrid inside the boundary box
  const hexagonalGrid = hexGrid(boundaryBox, hexSideLength);

  // Clip each hexagon based on the original polygon
  const clippedHexGrid = hexagonalGrid.features.map((hexagon, index) => {
    try {
      const clippedHexagon = intersect(hexagon, turfPolygon);
      return clippedHexagon ? clippedHexagon.geometry.coordinates : null;
    } catch (e) {
      console.log('failed to clip hex', hexagon, index);
      return null;
    }
  });

  const validHexagons = clippedHexGrid.filter(hexagon => hexagon !== null);
  return validHexagons;
};

export const establishSpatialRelationships = hexagons => {
  const spatialRelationships = {};

  // Iterate through hexagons
  hexagons.forEach((hexagon1, index1) => {
    const neighbors: number[] = [];
    // Check each other hexagon for adjacency
    hexagons.forEach((hexagon2, index2) => {
      if (index1 !== index2) {
        // Check if hexagons share an edge or a vertex
        const coordinates1 = hexagon1.coordinates;
        const coordinates2 = hexagon2.coordinates;

        if (coordinates1.length >= 4 && coordinates2.length >= 4) {
          //TODO: To be checked by shubhayan
          const sharedEdge = !booleanDisjoint(
            turf.polygon([coordinates1]),
            turf.polygon([coordinates2])
          );

          const sharedVertex = coordinates1.some(coord =>
            booleanPointInPolygon(turf.point(coord), turf.polygon([coordinates2]))
          );

          if (sharedEdge || sharedVertex) {
            neighbors.push(index2);
          }
        }
      }
    });

    spatialRelationships[index1] = neighbors;
  });

  return spatialRelationships;
};

export const bfsMergeHexagons = (unmergedHexagons, spatialRelationships, mergeThreshold) => {
  const hexagons = [...unmergedHexagons];
  const queue: number[] = [];
  const visited = new Set();
  unmergedHexagons.forEach((hexagon, index) => {
    if (hexagon.listings.length < mergeThreshold) {
      queue.push(index);
    }
  });

  while (queue.length > 0) {
    const currentHexagonIndex = queue.shift() as number;
    const currentHexagon = unmergedHexagons[currentHexagonIndex];
    const neighbors = spatialRelationships[currentHexagonIndex];

    // Sort neighbors based on listings count (ascending order)
    const sortedNeighbors = neighbors.sort((a, b) => {
      const countA = hexagons[a].listings.length;
      const countB = hexagons[b].listings.length;
      return countA - countB;
    });

    let mergedHexagon = currentHexagon;

    for (let neighborIndex of sortedNeighbors) {
      if (currentHexagon.listings.length >= mergeThreshold) {
        continue;
      }

      if (!visited.has(neighborIndex)) {
        const neighborHexagon = unmergedHexagons[neighborIndex];

        const poly1 = turf.polygon([currentHexagon.coordinates]);
        const poly2 = turf.polygon([neighborHexagon.coordinates]);

        try {
          const mergedPolygon = union(poly1, poly2);
          mergedHexagon = {
            coordinates: mergedPolygon?.geometry.coordinates[0],
            listings: [...mergedHexagon.listings, ...neighborHexagon.listings],
          };
        } catch (e) {
          console.log('Error in merging polygons: ' + e);
        }

        hexagons[currentHexagonIndex] = mergedHexagon;
        hexagons[neighborIndex] = { ...neighborHexagon, isMerged: true };
        visited.add(neighborIndex);

        if (mergedHexagon.listings.length >= mergeThreshold) {
          break;
        }
      }
    }
  }

  const filteredHexagons = hexagons.filter(hexagon => !hexagon.hasOwnProperty('isMerged'));
  return filteredHexagons;
};

export const getMedianOccupancyRate = (listings: ListingVal[]) => {
  const occupancyRates = listings.map(listing => listing.occupancyRateLTM);
  const median = d3.median(occupancyRates);
  return Math.round(median);
};

export const getMedianRevenue = (listings: ListingVal[]) => {
  const revenues = listings.map(listing => listing.revenuePotential);
  const median = d3.median(revenues);
  return Math.round(median);
};

export const getNaturalBreaks = (dataArray: number[]) => {
  let numBreaks = 4;
  if (dataArray.length < 4) {
    numBreaks = dataArray.length;
  }
  const jenksResult = ss.jenks(dataArray, numBreaks);
  const naturalBreaks = jenksResult.map(jenks => Math.round(jenks));
  return naturalBreaks;
};

export const getSubmarketColor = (value: number, thresholds: number[], key: string) => {
  if (!value && key !== 'occupancyRateYearly') return '#00000000';
  else if (value <= thresholds[0]) return NEIGHBOURHOOD_THEME[0];
  else if (value <= thresholds[1]) return NEIGHBOURHOOD_THEME[1];
  else if (value <= thresholds[2]) return NEIGHBOURHOOD_THEME[2];
  else if (value <= thresholds[3]) return NEIGHBOURHOOD_THEME[3];
  else return NEIGHBOURHOOD_THEME[4];
};

export const numberOfNeighbourhoodsWithinBound = (polygons, bounds) => {
  let count = 0;
  const boundaryPolygon = bboxPolygon([
    bounds.southWest.lng,
    bounds.southWest.lat,
    bounds.northEast.lng,
    bounds.northEast.lat,
  ]);

  for (const polygon of polygons) {
    const coordinates = polygon
      .getPath()
      .getArray()
      .map(latLng => [latLng.lng(), latLng.lat()]);
    if (coordinates.length == 0) continue;
    const turfPolygon = turf.polygon([coordinates]);

    if (booleanWithin(turfPolygon, boundaryPolygon)) {
      count += 1;
    }
  }

  return count;
};

export const comparePolygonLimitsToBoundary = (polygonLimits, boundary, pixelOffset = 0) => {
  const latPixelSize = (boundary.northEast.lat - boundary.southWest.lat) / 256; // assuming 256 pixels in a map
  const lngPixelSize = (boundary.northEast.lng - boundary.southWest.lng) / 256;

  const isLatInside =
    polygonLimits.minLat >= boundary.southWest.lat - latPixelSize * pixelOffset &&
    polygonLimits.maxLat <= boundary.northEast.lat + latPixelSize * pixelOffset;

  const isLngInside =
    polygonLimits.minLng >= boundary.southWest.lng - lngPixelSize * pixelOffset &&
    polygonLimits.maxLng <= boundary.northEast.lng + lngPixelSize * pixelOffset;

  const isLatOverflowing =
    polygonLimits.minLat < boundary.southWest.lat - latPixelSize * pixelOffset ||
    polygonLimits.maxLat > boundary.northEast.lat + latPixelSize * pixelOffset;

  const isLngOverflowing =
    polygonLimits.minLng < boundary.southWest.lng - lngPixelSize * pixelOffset ||
    polygonLimits.maxLng > boundary.northEast.lng + lngPixelSize * pixelOffset;

  return {
    isInside: isLatInside && isLngInside,
    isOverflowing: isLatOverflowing || isLngOverflowing,
  };
};

// export const getAmenityDistribution = (listings: Listing[]) => {
//   let amenityFreq: { [key: string]: number } = {};
//   Object.values(amenitiesObject).forEach(key => {
//     amenityFreq[key] = 0;
//   });
//   let amenityDistribution: BarGraphData[] = [];
//   let totalNumberOfListings = 0;

//   totalNumberOfListings = listings.length;
//   if (totalNumberOfListings === 0) {
//     return amenityDistribution;
//   }

//   listings.forEach((listing: Listing) => {
//     Object.keys(amenitiesObject).forEach(amenity => {
//       if (listing.amenities[amenity]) {
//         amenityFreq[amenitiesObject[amenity]] = amenityFreq[amenitiesObject[amenity]] + 1;
//       }
//     });
//   });

//   amenityDistribution = Object.keys(amenityFreq)
//     .map(amenity => {
//       return {
//         keyLong: amenity,
//         value: Math.round((amenityFreq[amenity] / totalNumberOfListings) * 100),
//       };
//     })
//     .sort((a, b) => b.value - a.value);

//   return amenityDistribution;
// };

export const calculatePolygonArea = vertices => {
  let n = vertices.length;
  let area = 0;

  for (let i = 0; i < n; i++) {
    let lng_i = vertices[i].lng;
    let lat_i = vertices[i].lat;
    let lng_next = vertices[(i + 1) % n].lng;
    let lat_next = vertices[(i + 1) % n].lat;

    area += lng_i * lat_next - lng_next * lat_i;
  }

  area *= 0.5; // Divide the area by 2

  return area;
};

export const getAmenityDistribution = (
  listings: Listing[],
  cachedData: { [key: string]: number } | null = null
) => {
  let amenityFreq: { [key: string]: number } = {};
  Object.values(amenitiesObject).forEach(key => {
    amenityFreq[key] = 0;
  });
  let amenityDistribution: BarGraphData[] = [];
  let totalNumberOfListings = 0;

  if (!cachedData) {
    totalNumberOfListings = listings.length;
    if (totalNumberOfListings === 0) {
      return amenityDistribution;
    }

    listings.forEach((listing: Listing) => {
      Object.keys(amenitiesObject).forEach(amenity => {
        if (listing.amenities[amenity]) {
          amenityFreq[amenitiesObject[amenity]] = amenityFreq[amenitiesObject[amenity]] + 1;
        }
      });
    });

    amenityDistribution = Object.keys(amenityFreq)
      .map(amenity => {
        return {
          keyLong: amenity,
          value: Math.round((amenityFreq[amenity] / totalNumberOfListings) * 100),
        };
      })
      .sort((a, b) => b.value - a.value);
  } else {
    amenityDistribution = Object.keys(cachedData)
      .map(amenity => ({
        keyLong: amenitiesObject[amenity],
        value: cachedData[amenity],
      }))
      .sort((a, b) => b.value - a.value);
  }

  return amenityDistribution;
};

export const sortListingBasedOnKey = (listings, key) => {
  if (key === 'performance') {
    return sortListingsByPerformance(listings);
  } else if (key === 'review') {
    return listings.sort((a, b) => {
      if (a.visible_review_count === 'N/A' && b.visible_review_count !== 'N/A') {
        return 1; // 'N/A' should come after other values
      } else if (a.visible_review_count !== 'N/A' && b.visible_review_count === 'N/A') {
        return -1; // Other values should come before 'N/A'
      } else {
        return b.visible_review_count - a.visible_review_count; // Normal sorting for other cases
      }
    });
  } else if (key === 'revenue') {
    return listings.sort((a, b) => {
      if (a.annualRevenueLTM === 'N/A') {
        return 1; // 'N/A' should come after other values
      }
      if (b.annualRevenueLTM === 'N/A') {
        return -1; // Other values should come before 'N/A'
      }
      // If neither is 'N/A', sort by annualRevenueLTM descending
      return b.annualRevenueLTM - a.annualRevenueLTM;
    });
  } else if (key === 'occupancy') {
    return listings.sort((a, b) => {
      if (a.occupancyRateLTM === 'N/A' && b.occupancyRateLTM !== 'N/A') {
        return 1; // 'N/A' should come after other values
      } else if (a.occupancyRateLTM !== 'N/A' && b.occupancyRateLTM === 'N/A') {
        return -1; // Other values should come before 'N/A'
      } else {
        // Both values are either 'N/A' or non-'N/A', sort by occupancyRateLTM value
        return b.occupancyRateLTM - a.occupancyRateLTM;
      }
    });
    return listings.sort((a, b) => b.occupancyRateLTM - a.occupancyRateLTM);
  } else {
    return listings.sort((a, b) => {
      if (a.adrLTM === 'N/A' && b.adrLTM === 'N/A') {
        return 0;
      } else if (a.adrLTM === 'N/A') {
        return 1;
      } else if (b.adrLTM === 'N/A') {
        return -1;
      } else {
        return b.adrLTM - a.adrLTM;
      }
    });
  }
};

export const sortListingsByPerformance = (listings: Listing[]) => {
  const sortedListings: Listing[] = [...listings];

  const getAvgRevPanByBedroom = (listings: Listing[]) => {
    const revPanByBedroom = {
      '0': [] as number[],
      '1': [] as number[],
      '2': [] as number[],
      '3': [] as number[],
      '4': [] as number[],
      '5': [] as number[],
      '6+': [] as number[],
      rooms: [] as number[],
    };

    listings.forEach((listing: Listing) => {
      const revPAN: number = listing.revPAN ? listing.revPAN : 0;
      if (
        ((listing.room_type && listing.room_type === 'entire_home') ||
          (listing.property_type && listing.property_type === 'condo')) &&
        (listing.bedrooms == 'Studio' || !isNaN(Number(listing.bedrooms)))
      ) {
        if (listing.bedrooms == 'Studio') {
          revPanByBedroom['0'].push(revPAN);
        } else if (Number(listing.bedrooms) >= 6) {
          revPanByBedroom['6+'].push(revPAN);
        } else {
          revPanByBedroom[listing.bedrooms].push(revPAN);
        }
      } else if (
        listing.room_type &&
        (listing.room_type === 'private_room' ||
          listing.room_type === 'hotel_room' ||
          listing.room_type === 'shared_room')
      ) {
        revPanByBedroom['rooms'].push(revPAN);
      }
    });

    const avgRevPanByBedroom: { [key: string]: number } = {};
    for (const bedroomType in revPanByBedroom) {
      const revPANList = revPanByBedroom[bedroomType];
      const totalRevPAN = revPANList.reduce((sum, revPAN) => sum + revPAN, 0);
      const avgRevPAN = revPANList.length > 0 ? totalRevPAN / revPANList.length : 0;
      avgRevPanByBedroom[bedroomType] = avgRevPAN;
    }
    return avgRevPanByBedroom;
  };

  const calculateRevPanDifference = (
    listing: Listing,
    avgRevPanByBedroom: { [key: string]: number }
  ): number => {
    const listingRevPAN = listing.revPAN || 0;
    let key = '';
    if (
      ((listing.room_type && listing.room_type === 'entire_home') ||
        (listing.property_type && listing.property_type === 'condo')) &&
      (listing.bedrooms == 'Studio' || !isNaN(Number(listing.bedrooms)))
    ) {
      if (listing.bedrooms == 'Studio') {
        key = '0';
      } else if (Number(listing.bedrooms) >= 6) {
        key = '6+';
      } else {
        key = listing.bedrooms;
      }
    } else if (
      listing.room_type &&
      (listing.room_type === 'private_room' ||
        listing.room_type === 'hotel_room' ||
        listing.room_type === 'shared_room')
    ) {
      key = 'rooms';
    }
    const avgRevPAN = key in avgRevPanByBedroom ? avgRevPanByBedroom[key] : 0;
    return listingRevPAN - avgRevPAN;
  };

  const avgRevPanByBedroom = getAvgRevPanByBedroom(listings);

  sortedListings.sort((a, b) => {
    try {
      // Calculate weighted scores for each listing
      const scoreA = calculatePerfScore(a);
      const scoreB = calculatePerfScore(b);

      // Compare weighted scores
      if (scoreA < scoreB) {
        return 1;
      } else if (scoreA > scoreB) {
        return -1;
      }

      // Break ties based on RevPAN
      const revPANComparison =
        calculateRevPanDifference(b, avgRevPanByBedroom) -
        calculateRevPanDifference(a, avgRevPanByBedroom);
      if (revPANComparison !== 0) {
        return revPANComparison;
      }
    } catch (e) {
      console.error('Weighted sorting function failed:', e);
    }

    return 0;
  });

  return sortedListings;
};

export const getScoreCardTooltipTabledata = (market, currency) => {
  let tableData: any = [];
  for (let year in market.ten_years_wealth_table) {
    const formattedYear = year.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    tableData.push({
      year: formattedYear,
      property_price:
        exchangeRates[currency].symbol +
        abbreviateNumber(
          convertCurrency(currency, market.ten_years_wealth_table[year].property_price)
        ),
      total_profit:
        exchangeRates[currency].symbol +
        abbreviateNumber(
          convertCurrency(currency, market.ten_years_wealth_table[year].total_profit)
        ),
      total_wealth:
        exchangeRates[currency].symbol +
        abbreviateNumber(
          convertCurrency(currency, market.ten_years_wealth_table[year].total_wealth)
        ),
    });
  }
  return tableData;
};

// export const simulateTenYearsROI = (propertyPrice, netRentalYield, annualAppreciation) => {
//   let totalProfit = 0;
//   let compoundedPropertyPrice = 0;

//   for (let year = 0; year < 10; year++) {
//     compoundedPropertyPrice = propertyPrice * Math.pow(1 + annualAppreciation / 100, year);
//     totalProfit += (netRentalYield / 100) * compoundedPropertyPrice;
//   }

//   let tenYearsROI = Math.round(((compoundedPropertyPrice + totalProfit) / propertyPrice) * 10) / 10;

//   return tenYearsROI;
// };

export const processAndSortMarketLevelData = (
  data: any,
  doNotSortFirstThreeMarket: boolean = false
) => {
  let sortedData: any = [];

  const dataArray = Object.entries(data);
  if (doNotSortFirstThreeMarket) {
    sortedData = dataArray.slice(0, 3).map(entry => entry[1]);
    const remainingData = dataArray.slice(3);
    remainingData.sort(([, a], [, b]) => (b as any).ten_years_roi - (a as any).ten_years_roi);
    sortedData.push(...remainingData.map(entry => entry[1]));
  } else {
    dataArray.sort(([, a], [, b]) => (b as any).ten_years_roi - (a as any).ten_years_roi);

    for (let data of dataArray) {
      sortedData.push(data[1]);
    }
  }

  return sortedData;
};

export const marketInSampleFreeMarkets = (market, freeMarketDataKey) => {
  const key = `${market.district}-${market.city}-${market.country_code}`;
  if (freeMarketDataKey.includes(key)) {
    return true;
  } else {
    return false;
  }
};

export const getMinMaxActiveListingsAndPropertyPrice = allRawMarketData => {
  let minActiveListingsCount = Number.POSITIVE_INFINITY;
  let maxActiveListingCount = Number.NEGATIVE_INFINITY;

  let minPropertyPrice = Number.POSITIVE_INFINITY;
  let maxPropertyPrice = Number.NEGATIVE_INFINITY;

  for (let data of allRawMarketData) {
    if (data.total_supply > maxActiveListingCount) {
      maxActiveListingCount = data.total_supply;
    }
    if (data.total_supply < minActiveListingsCount) {
      minActiveListingsCount = data.total_supply;
    }

    if (data.property_price > maxPropertyPrice) {
      maxPropertyPrice = data.property_price;
    }

    if (data.property_price < minPropertyPrice) {
      minPropertyPrice = data.property_price;
    }
  }

  return [minActiveListingsCount, maxActiveListingCount, minPropertyPrice, maxPropertyPrice];
};

export const getFormattedRank = (rank: number): string => {
  if (rank <= 100) {
    return rank.toString();
  }
  const batches = Math.floor((rank - 1) / 250);
  const start = Math.max(batches * 250 + 1, 100);
  const end = (batches + 1) * 250;

  return abbreviateNumber(start) + ' - ' + abbreviateNumber(end);
};

export const extractVisibleListingIDs = (visibleListings: Listing[] | OTAListing[]): string[] => {
  if (!visibleListings.length) return [];

  const visibleListingIDs: string[] = visibleListings.map(
    visibleListing => visibleListing['listingID']
  );

  return visibleListingIDs;
};

export const getListingIDFromURL = url => {
  const regex = /\/rooms\/(\d+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

export const isValidListingId = id => {
  const idRegex = /^\d+$/; // Listing ID should be digits only
  return idRegex.test(id);
};

const calculateDaysDiff = (lastSeen: string): number => {
  const currentDate = new Date();
  const lastSeenDate = new Date(lastSeen);

  const daysDifference = Math.floor(
    (currentDate.getTime() - lastSeenDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  return daysDifference;
};

export const calculatePerfScore = listing => {
  // TODO: should be returned from lambda
  const isRecentlyAdded = checkListingRecentlyAdded(listing);
  const isInactive = checkListingInactive(listing);
  const isUnlisted = checkListingUnlisted(listing);

  if (isInactive) {
    return -3;
  }

  if (isRecentlyAdded) {
    return -3;
  }

  if (isUnlisted) {
    return -2;
  }

  // top 10 ADR -1

  const occupancyRateLTM = listing.occupancyRateLTM;
  const activeDaysCount = listing.activeDaysCount;
  const numberOfReviews = listing.visible_review_count;
  const reviewScoresRating = listing.review_scores_rating;
  const lastSeen = listing.last_seen;
  let reviewCountPerActiveDay = 0;
  if (activeDaysCount > 0) {
    reviewCountPerActiveDay = numberOfReviews / activeDaysCount;
  }

  const lastSeenDaysDiff = calculateDaysDiff(lastSeen);

  // intitalization
  let occupancyRateScore = 0;
  let activeDaysCountScore = 0;
  let reviewCountPerActiveDayScore = 0;
  let ratingScore = 0;
  let lastSeenScore = 0;

  // score calculation
  if (occupancyRateLTM > 50) {
    occupancyRateScore = 2;
  } else {
    occupancyRateScore = 1;
  }

  if (activeDaysCount >= 240) {
    activeDaysCountScore = 2;
  } else if (activeDaysCount >= 140) {
    activeDaysCountScore = 1;
  } else {
    activeDaysCountScore = 0;
  }

  if (reviewCountPerActiveDay >= 2) {
    reviewCountPerActiveDayScore = 2;
  } else if (reviewCountPerActiveDay >= 1) {
    reviewCountPerActiveDayScore = 1;
  } else {
    reviewCountPerActiveDayScore = 0;
  }

  if (reviewScoresRating >= 4.5) {
    ratingScore = 2;
  } else if (reviewScoresRating >= 4.25) {
    ratingScore = 1;
  } else {
    ratingScore = 0;
  }

  if (lastSeenDaysDiff < 30) {
    lastSeenScore = 2;
  } else if (lastSeenDaysDiff <= 180) {
    lastSeenScore = 1;
  } else {
    lastSeenScore = 0;
  }

  const perfScore =
    occupancyRateScore +
    activeDaysCountScore +
    reviewCountPerActiveDayScore +
    ratingScore +
    lastSeenScore;

  return perfScore;
};
